import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiX, FiMapPin } from 'react-icons/fi';
import { addShipment, getShippingLineList, getAllClients, getPorts } from '../../ApiController/ApiService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import LocationPickerModal from './LocationPickerModal';

export default function ExportBookingWizard({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shippingLines, setShippingLines] = useState([]);
  const [clientList, setClientList] = useState([]);

  // Autocomplete States
  const [isSearchingDelivery, setIsSearchingDelivery] = useState(false);
  const [deliverySuggestions, setDeliverySuggestions] = useState([]);

  const [isSearchingPickupPort, setIsSearchingPickupPort] = useState(false);
  const [pickupPortSuggestions, setPickupPortSuggestions] = useState([]);

  const [isSearchingReturnPort, setIsSearchingReturnPort] = useState(false);
  const [returnPortSuggestions, setReturnPortSuggestions] = useState([]);

  const [openMapFor, setOpenMapFor] = useState(null);

  const [isPickupDropdownOpen, setIsPickupDropdownOpen] = useState(false);
  const [pickupSearchInput, setPickupSearchInput] = useState('');
  
  const [isReturnDropdownOpen, setIsReturnDropdownOpen] = useState(false);
  const [returnSearchInput, setReturnSearchInput] = useState('');

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [linesRes, clientsRes] = await Promise.all([
          getShippingLineList(),
          getAllClients({ limit: 100 })
        ]);
        setShippingLines(linesRes || []);
        
        let clientsArray = [];
        if (clientsRes?.success) {
          if (Array.isArray(clientsRes.data?.data?.docs)) {
            clientsArray = clientsRes.data.data.docs;
          } else if (Array.isArray(clientsRes.data?.data)) {
            clientsArray = clientsRes.data.data;
          } else if (Array.isArray(clientsRes.data?.docs)) {
            clientsArray = clientsRes.data.docs;
          } else if (Array.isArray(clientsRes.data)) {
            clientsArray = clientsRes.data;
          }
        }
        setClientList(clientsArray);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchDropdownData();
  }, []);

  const [formData, setFormData] = useState({
    importExportType: 'export',
    bookingNumber: '',
    shippingLine: '',
    price: '',
    totalNumberOfContainer: 1,
    containerType: '',

    customerDetails: {
      customerType: 'Shipper',
      customerId: '',
      customerCompanyName: '',
      customerContactName: '',
      customerEmail: '',
      customerAddress: ''
    },

    deliveryDetails: {
      receiverCompanyName: '',
      contactPerson: '',
      phone: '',
      email: '',
      address: '',
      instructions: '',
      appointmentDate: ''
    },

    bookingDetails: {
      vessel: '',
      voyage: '',
      cutoffDate: '',
      cutoffTime: '',
      returnLastReceivingDate: '',
      emptyPickupLocation: {
        portCode: '',
        portName: '',
        appointmentTime: '',
        address: '',
        instructions: '',
        contactPerson: '',
        contactNumber: ''
      },
      emptyReturnLocation: {
        portCode: '',
        portName: '',
        appointmentTime: '',
        address: '',
        instructions: '',
        contactPerson: '',
        contactNumber: ''
      }
    },
    
    deliveryLat: '',
    deliveryLng: '',
    pickupLat: '',
    pickupLng: '',
    returnLat: '',
    returnLng: ''
  });

  // Autocomplete for Delivery
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (formData.deliveryDetails.address && formData.deliveryDetails.address.trim().length > 2 && !formData.deliveryLat) {
        setIsSearchingDelivery(true);
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.deliveryDetails.address)}&limit=5`);
          const data = await response.json();
          setDeliverySuggestions(data || []);
        } catch (error) {
          setDeliverySuggestions([]);
        } finally {
          setIsSearchingDelivery(false);
        }
      } else {
        setDeliverySuggestions([]);
      }
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [formData.deliveryDetails.address, formData.deliveryLat]);

  // Autocomplete for Pickup Port
  useEffect(() => {
    if (!isPickupDropdownOpen) return;
    const delayDebounceFn = setTimeout(async () => {
      setIsSearchingPickupPort(true);
      try {
        const data = await getPorts('', pickupSearchInput);
        let portsArray = Array.isArray(data) ? data : data.data || [];
        setPickupPortSuggestions(portsArray);
      } catch (error) {
        setPickupPortSuggestions([]);
      } finally {
        setIsSearchingPickupPort(false);
      }
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [pickupSearchInput, isPickupDropdownOpen]);

  // Autocomplete for Return Port
  useEffect(() => {
    if (!isReturnDropdownOpen) return;
    const delayDebounceFn = setTimeout(async () => {
      setIsSearchingReturnPort(true);
      try {
        const data = await getPorts('', returnSearchInput);
        let portsArray = Array.isArray(data) ? data : data.data || [];
        setReturnPortSuggestions(portsArray);
      } catch (error) {
        setReturnPortSuggestions([]);
      } finally {
        setIsSearchingReturnPort(false);
      }
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [returnSearchInput, isReturnDropdownOpen]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleDeepNestedChange = (parent, child, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: {
          ...prev[parent][child],
          [field]: value
        }
      }
    }));
  };

  const validateForm = () => {
    if (!formData.bookingNumber) {
      toast.error('Booking Number is required', { position: 'top-right' });
      return false;
    }
    if (!formData.shippingLine) {
      toast.error('Steamship Line is required', { position: 'top-right' });
      return false;
    }
    if (!formData.bookingDetails.cutoffDate) {
      toast.error('Cutoff Date is required', { position: 'top-right' });
      return false;
    } else {
      const cutoff = new Date(formData.bookingDetails.cutoffDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (cutoff < today) {
        toast.error('Cutoff Date cannot be in the past', { position: 'top-right' });
        return false;
      }
    }
    if (!formData.totalNumberOfContainer || formData.totalNumberOfContainer < 1) {
      toast.error('Total number of containers must be at least 1', { position: 'top-right' });
      return false;
    }
    if (!formData.containerType) {
      toast.error('Container Type is required', { position: 'top-right' });
      return false;
    }
    if (!formData.bookingDetails.emptyPickupLocation.portCode || !formData.bookingDetails.emptyPickupLocation.appointmentTime) {
      toast.error('Please select a valid Pickup Port and Appointment Time', { position: 'top-right' });
      return false;
    }
    if (!formData.bookingDetails.emptyPickupLocation.address) {
      toast.error('Pickup Address is mandatory', { position: 'top-right' });
      return false;
    }
    if (!formData.bookingDetails.emptyReturnLocation.portCode || !formData.bookingDetails.emptyReturnLocation.appointmentTime) {
      toast.error('Please select a valid Return Port and Appointment Time', { position: 'top-right' });
      return false;
    }
    if (!formData.bookingDetails.emptyReturnLocation.address) {
      toast.error('Return Address is mandatory', { position: 'top-right' });
      return false;
    }
    if (!formData.deliveryDetails.address || !formData.deliveryLat) {
      toast.error('Please select a valid Delivery Address from the map suggestions', { position: 'top-right' });
      return false;
    }
    // Client-side mirror of backend rule: returnLastReceivingDate cannot be after cutoffDate
    if (formData.bookingDetails.returnLastReceivingDate && formData.bookingDetails.cutoffDate) {
      const returnDate = new Date(formData.bookingDetails.returnLastReceivingDate);
      const cutoff = new Date(formData.bookingDetails.cutoffDate);
      cutoff.setHours(23, 59, 59, 999); // Set to end of day to avoid same-day failure
      if (returnDate > cutoff) {
        toast.error('Return Last Receiving Date cannot be after the Cutoff Date', { position: 'top-right' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        importExportType: formData.importExportType,
        bookingNumber: formData.bookingNumber,
        totalNumberOfContainer: String(formData.totalNumberOfContainer),
        containerType: formData.containerType,
        shippingLine: formData.shippingLine,
        price: formData.price ? parseFloat(formData.price) : undefined,
        
        customerDetails: {
          customerType: formData.customerDetails.customerType,
          customerId: formData.customerDetails.customerId,
          customerCompanyName: formData.customerDetails.customerCompanyName,
          customerContactName: formData.customerDetails.customerContactName,
          customerEmail: formData.customerDetails.customerEmail,
          customerAddress: formData.customerDetails.customerAddress
        },

        deliveryDetails: {
          receiverCompanyName: formData.deliveryDetails.receiverCompanyName,
          contactPerson: formData.deliveryDetails.contactPerson,
          phone: formData.deliveryDetails.phone,
          email: formData.deliveryDetails.email,
          address: formData.deliveryDetails.address,
          instructions: formData.deliveryDetails.instructions,
          appointmentDate: formData.deliveryDetails.appointmentDate 
            ? (formData.deliveryDetails.appointmentDate.length === 16 
                ? formData.deliveryDetails.appointmentDate + ':00' 
                : formData.deliveryDetails.appointmentDate)
            : ''
        },

        bookingDetails: {
          vessel: formData.bookingDetails.vessel,
          voyage: formData.bookingDetails.voyage,
          cutoffDate: formData.bookingDetails.cutoffDate,
          cutoffTime: formData.bookingDetails.cutoffTime,
          returnLastReceivingDate: formData.bookingDetails.returnLastReceivingDate 
            ? (formData.bookingDetails.returnLastReceivingDate.length === 16 
                ? formData.bookingDetails.returnLastReceivingDate + ':00' 
                : formData.bookingDetails.returnLastReceivingDate)
            : '',
          emptyPickupLocation: {
            portCode: formData.bookingDetails.emptyPickupLocation.portCode,
            portName: formData.bookingDetails.emptyPickupLocation.portName,
            appointmentTime: formData.bookingDetails.emptyPickupLocation.appointmentTime
              ? new Date(formData.bookingDetails.emptyPickupLocation.appointmentTime).toISOString()
              : '',
            address: formData.bookingDetails.emptyPickupLocation.address,
            instructions: formData.bookingDetails.emptyPickupLocation.instructions,
            contactPerson: formData.bookingDetails.emptyPickupLocation.contactPerson,
            contactNumber: formData.bookingDetails.emptyPickupLocation.contactNumber
          },
          emptyReturnLocation: {
            portCode: formData.bookingDetails.emptyReturnLocation.portCode,
            portName: formData.bookingDetails.emptyReturnLocation.portName,
            appointmentTime: formData.bookingDetails.emptyReturnLocation.appointmentTime
              ? new Date(formData.bookingDetails.emptyReturnLocation.appointmentTime).toISOString()
              : '',
            address: formData.bookingDetails.emptyReturnLocation.address,
            instructions: formData.bookingDetails.emptyReturnLocation.instructions,
            contactPerson: formData.bookingDetails.emptyReturnLocation.contactPerson,
            contactNumber: formData.bookingDetails.emptyReturnLocation.contactNumber
          }
        }
      };

      if (formData.deliveryLat && formData.deliveryLng) {
        payload.deliveryDetails.location = {
          type: "Point",
          coordinates: [parseFloat(formData.deliveryLng), parseFloat(formData.deliveryLat)]
        };
        payload.destinationLocation = {
          type: "Point",
          coordinates: [parseFloat(formData.deliveryLng), parseFloat(formData.deliveryLat)]
        };
      }

      if (formData.pickupLat && formData.pickupLng) {
        payload.bookingDetails.emptyPickupLocation.location = {
          type: "Point",
          coordinates: [parseFloat(formData.pickupLng), parseFloat(formData.pickupLat)]
        };
        payload.currentLocation = {
          type: "Point",
          coordinates: [parseFloat(formData.pickupLng), parseFloat(formData.pickupLat)]
        };
      }

      if (formData.returnLat && formData.returnLng) {
        payload.bookingDetails.emptyReturnLocation.location = {
          type: "Point",
          coordinates: [parseFloat(formData.returnLng), parseFloat(formData.returnLat)]
        };
      }

      const res = await addShipment(payload);
      if (res && res.data) {
        toast.success('Export booking created successfully', { position: 'top-right' });
        if (onSuccess) onSuccess(res.data);
        if (onClose) onClose();
      } else {
        toast.error('Failed to create booking', { position: 'top-right' });
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to submit booking', { position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6 text-white bg-transparent">

      {/* Booking Details */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">{t('orders.bookingDetails', 'Booking Details')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm block mb-1 text-gray-300">Booking Number*</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingNumber}
              onChange={(e) => handleInputChange('bookingNumber', e.target.value)}
              placeholder="e.g. MSCU123456"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.shippingLine', 'Steamship Line')}*</label>
            <select
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.shippingLine}
              onChange={(e) => handleInputChange('shippingLine', e.target.value)}
            >
              <option value="">{t('orders.selectShippingLine', 'Select Steamship Line')}</option>
              {shippingLines.map((line, index) => (
                <option key={index} value={line._id || line.id || line.name || line}>
                  {line.name || line}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.priceOptional', 'Price (Optional)')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Vessel</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.vessel}
              onChange={(e) => handleNestedChange('bookingDetails', 'vessel', e.target.value)}
              placeholder="e.g. MAERSK HOUSTON"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Voyage</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.voyage}
              onChange={(e) => handleNestedChange('bookingDetails', 'voyage', e.target.value)}
              placeholder="e.g. 351W"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Cutoff Date*</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              type="date"
              value={formData.bookingDetails.cutoffDate}
              onChange={(e) => handleNestedChange('bookingDetails', 'cutoffDate', e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Cutoff Time</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              type="time"
              value={formData.bookingDetails.cutoffTime}
              onChange={(e) => handleNestedChange('bookingDetails', 'cutoffTime', e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Return Last Receiving Date</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              type="datetime-local"
              value={formData.bookingDetails.returnLastReceivingDate}
              onChange={(e) => handleNestedChange('bookingDetails', 'returnLastReceivingDate', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Container Requirements */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">{t('orders.containerRequirements', 'Container Requirements')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.totalContainers', 'Total Containers*')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
              type="number"
              min={1}
              value={formData.totalNumberOfContainer}
              onChange={(e) => handleInputChange('totalNumberOfContainer', parseInt(e.target.value, 10))}
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Container Type*</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
              type="text"
              value={formData.containerType}
              onChange={(e) => handleInputChange('containerType', e.target.value)}
              placeholder="e.g. 40HC"
            />
          </div>
        </div>
      </div>

      {/* Empty Pickup Location */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">Empty Container Pickup</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-full relative">
            <label className="text-sm block mb-1 text-gray-300">Pickup Port*</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div 
                  className="w-full px-3 py-2 rounded bg-white text-sm border border-gray-300 cursor-pointer flex justify-between items-center"
                  onClick={() => setIsPickupDropdownOpen(!isPickupDropdownOpen)}
                >
                  <span className={formData.bookingDetails.emptyPickupLocation.portName ? 'text-black font-semibold' : 'text-gray-400'}>
                    {formData.bookingDetails.emptyPickupLocation.portName || "Select Port"}
                  </span>
                  <span className="text-gray-500 text-[10px]">▼</span>
                </div>
                
                {isPickupDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-1 bg-white shadow-2xl rounded-md z-50 text-black border border-gray-200">
                    <div className="p-2 border-b border-gray-200 relative">
                      <input 
                        type="text"
                        autoFocus
                        className="w-full px-2 py-1.5 border border-gray-300 rounded outline-none text-sm"
                        placeholder="Search port (e.g. oak)..."
                        value={pickupSearchInput}
                        onChange={(e) => setPickupSearchInput(e.target.value)}
                      />
                      {isSearchingPickupPort && (
                        <div className="absolute right-4 top-4 w-4 h-4 border-2 border-t-[#6F1AFF] border-gray-300 rounded-full animate-spin"></div>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar">
                      {pickupPortSuggestions.length > 0 ? (
                        pickupPortSuggestions.map((suggestion, index) => (
                          <div 
                            key={index} 
                            className="px-3 py-2 text-sm hover:bg-violet-50 cursor-pointer border-b last:border-b-0 border-gray-100 transition-colors"
                            onClick={() => {
                              handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'portName', suggestion.portName);
                              handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'portCode', suggestion.portCode);
                              setIsPickupDropdownOpen(false);
                              setPickupSearchInput('');
                              setPickupPortSuggestions([]);
                            }}
                          >
                            <span className="font-semibold">{suggestion.portName}</span> <span className="text-gray-500 text-xs">({suggestion.portCode})</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No ports found.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {formData.bookingDetails.emptyPickupLocation.portCode && (
              <div className="text-[10px] text-gray-400 mt-1">Selected Port Code: {formData.bookingDetails.emptyPickupLocation.portCode}</div>
            )}
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Appointment Date & Time*</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
              type="datetime-local"
              value={formData.bookingDetails.emptyPickupLocation.appointmentTime}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'appointmentTime', e.target.value)}
            />
          </div>
          <div className="col-span-full relative">
            <label className="text-sm block mb-1 text-gray-300">Address*</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
                  value={formData.bookingDetails.emptyPickupLocation.address}
                  onChange={(e) => {
                    handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'address', e.target.value);
                    handleInputChange('pickupLat', '');
                    handleInputChange('pickupLng', '');
                  }}
                  placeholder="Address"
                />
              </div>
              <button
                type="button"
                onClick={() => setOpenMapFor('pickup')}
                className="bg-blue-600 hover:bg-blue-700 px-4 rounded-lg flex items-center justify-center text-white transition shadow"
                title="Select from Map"
              >
                <FiMapPin />
              </button>
            </div>
            {formData.pickupLat && formData.pickupLng && (
              <div className="text-[10px] text-gray-400 mt-1">
                {formData.pickupLat}, {formData.pickupLng}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Contact Person</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.emptyPickupLocation.contactPerson}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'contactPerson', e.target.value)}
              placeholder="Contact Person"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Contact Number</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.emptyPickupLocation.contactNumber}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'contactNumber', e.target.value)}
              placeholder="Contact Number"
            />
          </div>
          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">Pickup Instructions</label>
            <textarea
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none min-h-[60px]"
              value={formData.bookingDetails.emptyPickupLocation.instructions}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'instructions', e.target.value)}
              placeholder="Instructions..."
            />
          </div>
        </div>
      </div>

      {/* Empty Return Location */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">Return Full Container</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-full relative">
            <label className="text-sm block mb-1 text-gray-300">Return Port*</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div 
                  className="w-full px-3 py-2 rounded bg-white text-sm border border-gray-300 cursor-pointer flex justify-between items-center"
                  onClick={() => setIsReturnDropdownOpen(!isReturnDropdownOpen)}
                >
                  <span className={formData.bookingDetails.emptyReturnLocation.portName ? 'text-black font-semibold' : 'text-gray-400'}>
                    {formData.bookingDetails.emptyReturnLocation.portName || "Select Port"}
                  </span>
                  <span className="text-gray-500 text-[10px]">▼</span>
                </div>
                
                {isReturnDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-1 bg-white shadow-2xl rounded-md z-50 text-black border border-gray-200">
                    <div className="p-2 border-b border-gray-200 relative">
                      <input 
                        type="text"
                        autoFocus
                        className="w-full px-2 py-1.5 border border-gray-300 rounded outline-none text-sm"
                        placeholder="Search port (e.g. oak)..."
                        value={returnSearchInput}
                        onChange={(e) => setReturnSearchInput(e.target.value)}
                      />
                      {isSearchingReturnPort && (
                        <div className="absolute right-4 top-4 w-4 h-4 border-2 border-t-[#6F1AFF] border-gray-300 rounded-full animate-spin"></div>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar">
                      {returnPortSuggestions.length > 0 ? (
                        returnPortSuggestions.map((suggestion, index) => (
                          <div 
                            key={index} 
                            className="px-3 py-2 text-sm hover:bg-violet-50 cursor-pointer border-b last:border-b-0 border-gray-100 transition-colors"
                            onClick={() => {
                              handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'portName', suggestion.portName);
                              handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'portCode', suggestion.portCode);
                              setIsReturnDropdownOpen(false);
                              setReturnSearchInput('');
                              setReturnPortSuggestions([]);
                            }}
                          >
                            <span className="font-semibold">{suggestion.portName}</span> <span className="text-gray-500 text-xs">({suggestion.portCode})</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No ports found.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {formData.bookingDetails.emptyReturnLocation.portCode && (
              <div className="text-[10px] text-gray-400 mt-1">Selected Port Code: {formData.bookingDetails.emptyReturnLocation.portCode}</div>
            )}
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Appointment Date & Time*</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
              type="datetime-local"
              value={formData.bookingDetails.emptyReturnLocation.appointmentTime}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'appointmentTime', e.target.value)}
            />
          </div>
          <div className="col-span-full relative">
            <label className="text-sm block mb-1 text-gray-300">Address*</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
                  value={formData.bookingDetails.emptyReturnLocation.address}
                  onChange={(e) => {
                    handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'address', e.target.value);
                    handleInputChange('returnLat', '');
                    handleInputChange('returnLng', '');
                  }}
                  placeholder="Address"
                />
              </div>
              <button
                type="button"
                onClick={() => setOpenMapFor('return')}
                className="bg-blue-600 hover:bg-blue-700 px-4 rounded-lg flex items-center justify-center text-white transition shadow"
                title="Select from Map"
              >
                <FiMapPin />
              </button>
            </div>
            {formData.returnLat && formData.returnLng && (
              <div className="text-[10px] text-gray-400 mt-1">
                {formData.returnLat}, {formData.returnLng}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Contact Person</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.emptyReturnLocation.contactPerson}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'contactPerson', e.target.value)}
              placeholder="Contact Person"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Contact Number</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.bookingDetails.emptyReturnLocation.contactNumber}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'contactNumber', e.target.value)}
              placeholder="Contact Number"
            />
          </div>
          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">Return Instructions</label>
            <textarea
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none min-h-[60px]"
              value={formData.bookingDetails.emptyReturnLocation.instructions}
              onChange={(e) => handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'instructions', e.target.value)}
              placeholder="Instructions..."
            />
          </div>
        </div>
      </div>

      {/* Dispatch & Delivery Info */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">{t('orders.dispatchDeliveryInfo', 'Dispatch & Delivery Info')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.receiverCompany', 'Receiver Company')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.deliveryDetails.receiverCompanyName}
              onChange={(e) => handleNestedChange('deliveryDetails', 'receiverCompanyName', e.target.value)}
              placeholder={t('orders.receiverCompanyPlaceholder', 'Warehouse/Receiver Name')}
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.contactPerson', 'Contact Person')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.deliveryDetails.contactPerson}
              onChange={(e) => handleNestedChange('deliveryDetails', 'contactPerson', e.target.value)}
              placeholder={t('orders.contactPersonPlaceholder', 'Receiver Contact')}
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.phone', 'Phone')}</label>
            <div className="text-black">
              <PhoneInput
                country={'us'}
                value={formData.deliveryDetails.phone}
                onChange={(phone) => handleNestedChange('deliveryDetails', 'phone', phone)}
                inputStyle={{ width: '100%', height: '38px' }}
              />
            </div>
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.email', 'Email')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.deliveryDetails.email}
              onChange={(e) => handleNestedChange('deliveryDetails', 'email', e.target.value)}
              placeholder={t('orders.emailPlaceholder', 'Email Address')}
            />
          </div>
          <div className="col-span-full relative">
            <label className="text-sm block mb-1 text-gray-300">{t('orders.deliveryAddress', 'Delivery Address*')}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
                  value={formData.deliveryDetails.address}
                  onChange={(e) => {
                    handleNestedChange('deliveryDetails', 'address', e.target.value);
                    handleInputChange('deliveryLat', '');
                    handleInputChange('deliveryLng', '');
                  }}
                  placeholder={t('orders.deliveryAddressPlaceholder', 'Full Address')}
                />
                {isSearchingDelivery && (
                  <div className="absolute right-3 top-2 w-4 h-4 border-2 border-t-[#6F1AFF] border-gray-300 rounded-full animate-spin"></div>
                )}
                
                {deliverySuggestions.length > 0 && (
                  <div className="absolute top-12 left-0 w-full mt-1 bg-white shadow-2xl rounded-md z-50 max-h-48 overflow-y-auto text-black border border-gray-200">
                    {deliverySuggestions.map((suggestion, index) => (
                      <div 
                        key={index} 
                        className="px-3 py-2 text-sm hover:bg-violet-50 cursor-pointer border-b last:border-b-0 border-gray-100 transition-colors"
                        onClick={() => {
                          handleNestedChange('deliveryDetails', 'address', suggestion.display_name);
                          handleInputChange('deliveryLat', String(suggestion.lat));
                          handleInputChange('deliveryLng', String(suggestion.lon));
                          setDeliverySuggestions([]);
                        }}
                      >
                        {suggestion.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpenMapFor('delivery')}
                className="bg-blue-600 hover:bg-blue-700 px-4 rounded-lg flex items-center justify-center text-white transition shadow"
                title="Select from Map"
              >
                <FiMapPin />
              </button>
            </div>
            {formData.deliveryLat && formData.deliveryLng && (
              <div className="text-[10px] text-gray-400 mt-1">
                {formData.deliveryLat}, {formData.deliveryLng}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">{t('orders.appointmentDate', 'Appointment Date')}</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              type="datetime-local"
              value={formData.deliveryDetails.appointmentDate}
              onChange={(e) => handleNestedChange('deliveryDetails', 'appointmentDate', e.target.value)}
            />
          </div>
          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">{t('orders.instructions', 'Instructions')}</label>
            <textarea
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none min-h-[60px]"
              value={formData.deliveryDetails.instructions}
              onChange={(e) => handleNestedChange('deliveryDetails', 'instructions', e.target.value)}
              placeholder={t('orders.instructionsPlaceholder', 'Delivery instructions...')}
            />
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div className="bg-[#101533] p-4 rounded-xl border border-[#2A2A4D]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 border-b border-[#2A2A4D] pb-2">Customer Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">Select Existing Customer (Optional)</label>
            <select
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none border border-gray-300"
              onChange={(e) => {
                const selected = clientList.find(c => c._id === e.target.value);
                if (selected) {
                  setFormData(prev => ({
                    ...prev,
                    customerDetails: {
                      customerType: prev.customerDetails.customerType || 'Shipper',
                      customerId: selected._id || '',
                      customerCompanyName: selected.companyName || selected.fullName || '',
                      customerContactName: selected.fullName || '',
                      customerEmail: selected.email || '',
                      customerAddress: selected.address || ''
                    }
                  }));
                }
              }}
            >
              <option value="">-- Select Client to Auto-fill --</option>
              {clientList.map((client, idx) => (
                <option key={idx} value={client._id}>
                  {client.fullName || client.email || client._id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Customer Type</label>
            <select
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.customerDetails.customerType}
              onChange={(e) => handleNestedChange('customerDetails', 'customerType', e.target.value)}
            >
              <option value="Shipper">Shipper</option>
              <option value="Forwarder">Forwarder</option>
              <option value="Consignee">Consignee</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">Company Name</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.customerDetails.customerCompanyName}
              onChange={(e) => handleNestedChange('customerDetails', 'customerCompanyName', e.target.value)}
              placeholder="Client Company"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Contact Person</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.customerDetails.customerContactName}
              onChange={(e) => handleNestedChange('customerDetails', 'customerContactName', e.target.value)}
              placeholder="Full Name"
            />
          </div>
          <div>
            <label className="text-sm block mb-1 text-gray-300">Email</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.customerDetails.customerEmail}
              onChange={(e) => handleNestedChange('customerDetails', 'customerEmail', e.target.value)}
              placeholder="Email Address"
            />
          </div>
          <div className="col-span-full">
            <label className="text-sm block mb-1 text-gray-300">Address</label>
            <input
              className="w-full px-3 py-2 rounded bg-white text-black text-sm outline-none"
              value={formData.customerDetails.customerAddress}
              onChange={(e) => handleNestedChange('customerDetails', 'customerAddress', e.target.value)}
              placeholder="Full Address"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-2 mb-6">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-3 border border-red-400 text-red-400 rounded-xl font-bold hover:bg-red-400/10 transition-all flex justify-center items-center gap-2"
        >
          <FiX /> {t('orders.cancel', 'Cancel')}
        </button>

        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-bold hover:opacity-90 shadow-lg transition-all flex justify-center items-center disabled:opacity-50"
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <span className="flex items-center gap-2"><FiCheck /> {t('orders.createBooking', 'Create Booking')}</span>
          )}
        </button>
      </div>

      <LocationPickerModal
        isOpen={!!openMapFor}
        onClose={() => setOpenMapFor(null)}
        title={openMapFor === 'pickup' ? 'Select Pickup Location' : openMapFor === 'return' ? 'Select Return Location' : 'Select Delivery Location'}
        onSave={(loc) => {
          if (openMapFor === 'delivery') {
            handleNestedChange('deliveryDetails', 'address', loc.address);
            handleInputChange('deliveryLat', String(loc.lat));
            handleInputChange('deliveryLng', String(loc.lng));
          } else if (openMapFor === 'pickup') {
            handleDeepNestedChange('bookingDetails', 'emptyPickupLocation', 'address', loc.address);
            handleInputChange('pickupLat', String(loc.lat));
            handleInputChange('pickupLng', String(loc.lng));
          } else if (openMapFor === 'return') {
            handleDeepNestedChange('bookingDetails', 'emptyReturnLocation', 'address', loc.address);
            handleInputChange('returnLat', String(loc.lat));
            handleInputChange('returnLng', String(loc.lng));
          }
        }}
      />
    </form>
  );
}
